import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import { Drawer, Spin, Alert, Tag, Tooltip, Button, message, Popconfirm, Empty } from "antd";
import {
  PhoneOutlined, MailOutlined, EnvironmentOutlined, LinkOutlined,
  SyncOutlined, StopOutlined, PlayCircleOutlined, WhatsAppOutlined,
  InstagramOutlined, GlobalOutlined, ShopOutlined, CalendarOutlined,
  CheckCircleOutlined, RocketOutlined, DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import customClientAxios from "../customAxios";
import {
  AdminLeadFullProfile,
  AdminLeadStageTransitions,
  AdminLeadResyncHubspot,
  AdminLeadOptOutNurture,
  AdminLeadResumeNurture,
  AdminNurtureMessages,
} from "../utils/ApiEndpoints";
import { fmtRupees } from "../PlatformAnalytics/paUtils";
import { getGatewaySub } from "../utils/utils";

// ── Styled ────────────────────────────────────────────────────────────────

const HeaderBlock = styled.div`
  padding: 4px 0 16px;
`;

const NameRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;

  h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.3px;
  }
`;

const StageBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: white;
  background: ${(p) => p.$bg || "linear-gradient(135deg, #6366f1, #8b5cf6)"};
  white-space: nowrap;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 12.5px;
  color: #64748b;

  span { display: inline-flex; align-items: center; gap: 5px; }
  svg { font-size: 13px; color: #94a3b8; }
`;

const Section = styled.div`
  margin-bottom: 22px;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;

  h3 {
    margin: 0;
    font-size: 11.5px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
  }
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: ${(p) => p.$cols || "repeat(2, 1fr)"};
  gap: 10px;
`;

const MiniCard = styled.div`
  padding: 12px;
  border-radius: 10px;
  background: ${(p) => p.$bg || "#f8fafc"};
  border: 1px solid ${(p) => p.$border || "#e2e8f0"};

  .label {
    font-size: 10.5px;
    color: ${(p) => p.$labelColor || "#64748b"};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .value {
    font-size: ${(p) => p.$big ? "20px" : "13.5px"};
    font-weight: ${(p) => p.$big ? "700" : "500"};
    color: ${(p) => p.$valueColor || "#0f172a"};
    line-height: 1.2;
    word-break: break-word;
  }
  .sub {
    font-size: 11px;
    color: #94a3b8;
    margin-top: 3px;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 0;
  font-size: 13px;
  color: #1e293b;
  svg { color: #94a3b8; font-size: 14px; }
  a { color: #2563eb; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
  padding-left: 18px;

  &::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 6px;
    bottom: 6px;
    width: 2px;
    background: #f1f5f9;
  }
`;

const TLItem = styled.div`
  position: relative;
  padding: 8px 0 8px 14px;

  &::before {
    content: "";
    position: absolute;
    left: -16px;
    top: 12px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(p) => p.$color || "#94a3b8"};
    border: 2px solid white;
    box-shadow: 0 0 0 2px ${(p) => p.$color || "#94a3b8"};
  }

  .top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 2px;
  }
  .label {
    font-size: 12.5px;
    font-weight: 600;
    color: #0f172a;
    text-transform: capitalize;
  }
  .when {
    font-size: 10.5px;
    color: #94a3b8;
    white-space: nowrap;
  }
  .meta {
    font-size: 11px;
    color: #64748b;
  }
`;

const ActionsBar = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 12px 0 4px;
`;

// ── Helpers ───────────────────────────────────────────────────────────────

const STAGE_VIS = {
  new:         { label: "Prospect",     bg: "linear-gradient(135deg, #94a3b8, #cbd5e1)" },
  contacted:   { label: "Contacted",    bg: "linear-gradient(135deg, #94a3b8, #cbd5e1)" },
  qualified:   { label: "Qualified",    bg: "linear-gradient(135deg, #a855f7, #c084fc)" },
  negotiating: { label: "Negotiating",  bg: "linear-gradient(135deg, #a855f7, #c084fc)" },
  won:         { label: "Onboarded",    bg: "linear-gradient(135deg, #3b82f6, #60a5fa)" },
  activated:   { label: "Activated",    bg: "linear-gradient(135deg, #f59e0b, #fbbf24)" },
  purchased:   { label: "Plan Purchased", bg: "linear-gradient(135deg, #059669, #10b981)" },
  lost:        { label: "Lost",         bg: "linear-gradient(135deg, #ef4444, #f87171)" },
};

const PHASE_LABEL = {
  brand_page: "Brand Page Created",
  event_created: "Event Created",
  first_sale: "First Sale",
  none: "Not yet",
};

// Revenue from the API is in PAISE — fmtRupees converts to rupees and applies
// the standard K/L/Cr short-scale formatting used across the admin app.
const inr = fmtRupees;

const relativeDays = (date) => {
  if (!date) return "—";
  const d = dayjs(date);
  const diff = dayjs().diff(d, "day");
  if (diff === 0) return "today";
  if (diff === 1) return "yesterday";
  if (diff < 7) return `${diff}d ago`;
  if (diff < 30) return `${Math.floor(diff / 7)}w ago`;
  if (diff < 365) return `${Math.floor(diff / 30)}mo ago`;
  return `${Math.floor(diff / 365)}y ago`;
};

const HUBSPOT_ACCOUNT_ID = process.env.REACT_APP_HUBSPOT_ACCOUNT_ID || "246130222";

// ── Component ─────────────────────────────────────────────────────────────

const LeadDrawer = ({ leadId, open, onClose, onUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [transitions, setTransitions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!leadId || !open) return;
    setLoading(true);
    setError(null);
    try {
      const [pr, tr, msgs] = await Promise.all([
        customClientAxios().get(AdminLeadFullProfile(leadId)),
        customClientAxios().get(AdminLeadStageTransitions(leadId)),
        customClientAxios().get(AdminNurtureMessages, { params: { leadId, limit: 20 } }),
      ]);
      setProfile(pr.data?.data || null);
      setTransitions(tr.data?.data || []);
      setMessages(msgs.data?.data?.messages || []);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [leadId, open]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const callAction = async (urlBuilder, label) => {
    setBusy(label);
    try {
      await customClientAxios().post(urlBuilder(leadId));
      message.success(`${label} done`);
      fetchAll();
      onUpdate?.();
    } catch (e) {
      message.error(e?.response?.data?.message || `${label} failed`);
    } finally {
      setBusy(null);
    }
  };

  if (!open) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      placement="right"
      title={null}
      headerStyle={{ display: "none" }}
      bodyStyle={{ padding: 24, background: "#fff" }}
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 80 }}>
          <Spin size="large" />
        </div>
      )}

      {error && <Alert type="error" message={error} showIcon />}

      {!loading && !error && profile && (
        <>
          {/* Header */}
          <HeaderBlock>
            <NameRow>
              <div>
                <h2>{profile.lead.name || "Unknown"}</h2>
                <Meta style={{ marginTop: 6 }}>
                  {profile.lead.phone && <span><PhoneOutlined /> {profile.lead.phone}</span>}
                  {profile.lead.email && <span><MailOutlined /> {profile.lead.email}</span>}
                  {profile.lead.city && <span><EnvironmentOutlined /> {profile.lead.city}</span>}
                </Meta>
              </div>
              <StageBadge $bg={STAGE_VIS[profile.lead.stage]?.bg}>
                {STAGE_VIS[profile.lead.stage]?.label || profile.lead.stage}
              </StageBadge>
            </NameRow>

            <Meta>
              <span>Source: <strong style={{ color: "#475569", textTransform: "capitalize" }}>{profile.lead.source || "—"}</strong></span>
              <span>Created: <strong style={{ color: "#475569" }}>{relativeDays(profile.lead.createdAt)}</strong></span>
              <span>Updated: <strong style={{ color: "#475569" }}>{relativeDays(profile.lead.updatedAt)}</strong></span>
            </Meta>
          </HeaderBlock>

          {/* Actions */}
          <ActionsBar>
            {profile.lead.hubspotContactId && (
              <Button
                size="small"
                icon={<LinkOutlined />}
                href={`https://app.hubspot.com/contacts/${HUBSPOT_ACCOUNT_ID}/contact/${profile.lead.hubspotContactId}`}
                target="_blank"
              >
                View in HubSpot
              </Button>
            )}
            <Button
              size="small"
              icon={<SyncOutlined spin={busy === "Resync"} />}
              loading={busy === "Resync"}
              onClick={() => callAction(AdminLeadResyncHubspot, "Resync")}
            >
              Resync HubSpot
            </Button>
            {profile.lead.optedOutOfNurture ? (
              <Button
                size="small"
                icon={<PlayCircleOutlined />}
                loading={busy === "Resume"}
                onClick={() => callAction(AdminLeadResumeNurture, "Resume")}
              >
                Resume Nurture
              </Button>
            ) : (
              <Popconfirm
                title="Opt out of nurture?"
                description="Cancels pending WA messages."
                onConfirm={() => callAction(AdminLeadOptOutNurture, "Opt out")}
              >
                <Button size="small" icon={<StopOutlined />} loading={busy === "Opt out"} danger>
                  Opt Out
                </Button>
              </Popconfirm>
            )}
          </ActionsBar>

          {/* Funnel position summary */}
          <Section>
            <SectionHead><h3>Funnel</h3></SectionHead>
            <Cards $cols="repeat(2, 1fr)">
              <MiniCard>
                <div className="label">Stage</div>
                <div className="value" style={{ textTransform: "capitalize" }}>{STAGE_VIS[profile.lead.stage]?.label || profile.lead.stage}</div>
                {profile.lead.activationPhase && profile.lead.stage === "activated" && (
                  <div className="sub">Phase: {PHASE_LABEL[profile.lead.activationPhase]}</div>
                )}
              </MiniCard>
              <MiniCard>
                <div className="label">Nurture</div>
                <div className="value" style={{ textTransform: "capitalize" }}>{profile.lead.nurtureStatus?.replace(/_/g, " ") || "—"}</div>
                {profile.lead.optedOutOfNurture && (
                  <div className="sub" style={{ color: "#dc2626" }}>Opted out</div>
                )}
              </MiniCard>
              <MiniCard>
                <div className="label">HubSpot</div>
                <div className="value">
                  {profile.lead.hubspotContactId ? (
                    <span style={{ color: "#059669" }}>✓ Synced</span>
                  ) : profile.lead.hubspotSyncError ? (
                    <span style={{ color: "#dc2626" }}>Error</span>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>Unsynced</span>
                  )}
                </div>
                {profile.lead.hubspotSyncError && (
                  <div className="sub" style={{ color: "#dc2626" }}>{profile.lead.hubspotSyncError.slice(0, 60)}</div>
                )}
              </MiniCard>
              <MiniCard>
                <div className="label">Instagram</div>
                <div className="value">
                  {profile.lead.hasLinkedInstagram ? (
                    <span style={{ color: "#e11d48" }}><InstagramOutlined /> @{profile.lead.instagramHandle || "linked"}</span>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>Not linked</span>
                  )}
                </div>
              </MiniCard>
            </Cards>
          </Section>

          {/* Brand Page (post-onboard) */}
          {profile.brandPage && (
            <Section>
              <SectionHead><h3>Brand Page</h3></SectionHead>
              <MiniCard $bg="rgba(99,102,241,0.05)" $border="rgba(99,102,241,0.2)">
                <div className="label" style={{ color: "#6366f1" }}>{profile.brandPage.name}</div>
                <div className="value" style={{ fontSize: 13 }}>
                  <a
                    href={`https://growezy.club/${profile.brandPage.subdomain}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    growezy.club/{profile.brandPage.subdomain}
                  </a>
                </div>
                <div className="sub">
                  Plan: <strong>{profile.brandPage.plan || "free"}</strong>
                  {getGatewaySub(profile.brandPage)?.status === "active" && (
                    <Tag color="green" style={{ marginLeft: 6 }}>Active</Tag>
                  )}
                </div>
              </MiniCard>
            </Section>
          )}

          {/* Events / Offerings */}
          {profile.offerings && profile.offerings.length > 0 && (
            <Section>
              <SectionHead>
                <h3>Events ({profile.offerings.length})</h3>
              </SectionHead>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {profile.offerings.slice(0, 5).map((ev) => (
                  <MiniCard key={ev._id}>
                    <div className="label" style={{ marginBottom: 4 }}>
                      {ev.category || "Event"}
                      {ev.publishedStatus && (
                        <Tag color={ev.publishedStatus === "published" ? "green" : "default"} style={{ marginLeft: 6, fontSize: 9 }}>
                          {ev.publishedStatus}
                        </Tag>
                      )}
                    </div>
                    <div className="value" style={{ fontSize: 13 }}>{ev.title || ev.name}</div>
                    {ev.publicUrl && (
                      <div className="sub">
                        <a href={ev.publicUrl} target="_blank" rel="noreferrer">View public page</a>
                      </div>
                    )}
                  </MiniCard>
                ))}
                {profile.offerings.length > 5 && (
                  <div style={{ fontSize: 11, color: "#94a3b8", textAlign: "center" }}>
                    + {profile.offerings.length - 5} more
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Revenue (only meaningful for activated/purchased) */}
          {profile.revenue && (profile.revenue.grossSales > 0 || profile.revenue.subscriptionPaid > 0) && (
            <Section>
              <SectionHead><h3>Revenue</h3></SectionHead>
              <Cards $cols="repeat(2, 1fr)">
                <MiniCard $bg="rgba(16,185,129,0.06)" $border="rgba(16,185,129,0.2)">
                  <div className="label" style={{ color: "#047857" }}>Gross sales</div>
                  <div className="value" style={{ fontSize: 18, color: "#047857" }}>{inr(profile.revenue.grossSales)}</div>
                  <div className="sub">{profile.revenue.ticketsSold} tickets sold</div>
                </MiniCard>
                <MiniCard $bg="rgba(99,102,241,0.06)" $border="rgba(99,102,241,0.2)">
                  <div className="label" style={{ color: "#4f46e5" }}>Growezy revenue</div>
                  <div className="value" style={{ fontSize: 18, color: "#4f46e5" }}>{inr(profile.revenue.totalGrowezyRevenue)}</div>
                  <div className="sub">Fee + subscription</div>
                </MiniCard>
                <MiniCard>
                  <div className="label">Platform fee</div>
                  <div className="value">{inr(profile.revenue.platformFee)}</div>
                </MiniCard>
                <MiniCard>
                  <div className="label">Creator payouts</div>
                  <div className="value">{inr(profile.revenue.payoutAmount)}</div>
                  <div className="sub">{inr(profile.revenue.settledAmount)} settled</div>
                </MiniCard>
                {profile.revenue.subscriptionPaid > 0 && (
                  <MiniCard $bg="rgba(245,158,11,0.06)" $border="rgba(245,158,11,0.2)">
                    <div className="label" style={{ color: "#b45309" }}>Subscription paid</div>
                    <div className="value" style={{ color: "#b45309" }}>{inr(profile.revenue.subscriptionPaid)}</div>
                  </MiniCard>
                )}
              </Cards>
            </Section>
          )}

          {/* Stage transitions audit */}
          <Section>
            <SectionHead>
              <h3>Stage Transitions ({transitions.length})</h3>
            </SectionHead>
            {transitions.length === 0 ? (
              <div style={{ color: "#94a3b8", fontSize: 12, fontStyle: "italic" }}>
                No transitions yet — lead is still in initial stage
              </div>
            ) : (
              <Timeline>
                {transitions.map((t) => (
                  <TLItem
                    key={t._id}
                    $color={STAGE_VIS[t.toStage]?.bg ? "#6366f1" : "#94a3b8"}
                  >
                    <div className="top">
                      <div className="label">
                        {t.fromStage || "init"} → {t.toStage}
                        {t.toActivationPhase && t.toActivationPhase !== "none" && (
                          <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 11, marginLeft: 6 }}>
                            ({t.toActivationPhase.replace(/_/g, " ")})
                          </span>
                        )}
                      </div>
                      <div className="when">
                        <Tooltip title={dayjs(t.triggeredAt).format("DD MMM YYYY HH:mm")}>
                          {relativeDays(t.triggeredAt)}
                        </Tooltip>
                      </div>
                    </div>
                    <div className="meta">
                      via <strong style={{ color: "#475569" }}>{t.triggeredBy}</strong>
                      {t.attributedSequenceKey && (
                        <span style={{ marginLeft: 8 }}>
                          · attributed to <strong style={{ color: "#7c3aed" }}>
                            {t.attributedSequenceKey} D{t.attributedStepDay}
                          </strong>
                        </span>
                      )}
                    </div>
                  </TLItem>
                ))}
              </Timeline>
            )}
          </Section>

          {/* Recent nurture messages */}
          {messages.length > 0 && (
            <Section>
              <SectionHead>
                <h3>Recent WA Messages ({messages.length})</h3>
              </SectionHead>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, maxHeight: 280, overflowY: "auto" }}>
                {messages.map((m) => (
                  <div
                    key={m._id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 10px",
                      borderRadius: 8,
                      background: "#f8fafc",
                      border: "1px solid #f1f5f9",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <div style={{ fontSize: 11.5, fontWeight: 600, color: "#1e293b" }}>
                        {m.sequenceKey || "prospect"} <span style={{ color: "#94a3b8", fontWeight: 400 }}>D{m.stepDay}</span>
                      </div>
                      <div style={{ fontSize: 10.5, color: "#64748b" }}>
                        {relativeDays(m.sentAt || m.createdAt)}
                      </div>
                    </div>
                    <Tag color={
                      m.status === "read" ? "green" :
                      m.status === "delivered" ? "blue" :
                      m.status === "sent" ? "geekblue" :
                      m.status === "failed" ? "red" :
                      m.status === "skipped" ? "orange" : "default"
                    } style={{ margin: 0 }}>
                      {m.status}
                    </Tag>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      )}
    </Drawer>
  );
};

export default LeadDrawer;
